import React, { useState, useEffect } from "react";
import { Form, Radio, Select, Space, Spin } from "antd";
import StepHeader from "../../../Evaluation/StepHeader";
import { usePermissions } from "../../../../PermissionsProvider";
import { IntentService } from "../../../../services/IntentService";
import { ApplicationService } from "../../../../services/ApplicationService";
import { DatasetData } from "../../../../model_gen/dataset";

interface DatasetBaseTypeProps {
  baseType?: "intent_group" | "application_intent";
  intentGroupId?: number;
  applicationId?: number;
  intentId?: number;
  ignoredApplicationIds?: number[];
  onChange: (data: Partial<DatasetData>) => void;
}

const DatasetBaseType: React.FC<DatasetBaseTypeProps> = ({
  baseType,
  intentGroupId,
  applicationId,
  intentId,
  ignoredApplicationIds,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [loadingIntentGroups, setLoadingIntentGroups] =
    useState<boolean>(false);
  const [loadingApplications, setLoadingApplications] =
    useState<boolean>(false);
  const [loadingIntents, setLoadingIntents] = useState<boolean>(false);
  const [intentGroups, setIntentGroups] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [intents, setIntents] = useState<any[]>([]);

  // Fetch intent groups
  useEffect(() => {
    if (baseType === "intent_group" && apiKey) {
      setLoadingIntentGroups(true);
      IntentService.getIntentGroups(
        0,
        100,
        setLoadingIntentGroups,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intent groups response:", response);
          if (response && Array.isArray(response)) {
            setIntentGroups(response);
          } else if (response && Array.isArray(response.intent_groups)) {
            setIntentGroups(response.intent_groups);
          } else {
            console.error(
              "Unexpected intent groups response format:",
              response
            );
            setIntentGroups([]);
          }
          setLoadingIntentGroups(false);
        },
        (error: any) => {
          console.error("Error fetching intent groups:", error);
          setLoadingIntentGroups(false);
        }
      );
    }
  }, [baseType, apiKey]);

  // Fetch applications
  useEffect(() => {
    if (baseType === "application_intent" && apiKey) {
      setLoadingApplications(true);
      ApplicationService.getApplications(
        setLoadingApplications,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Applications response:", response);
          if (response && Array.isArray(response)) {
            setApplications(response);
          } else if (response && Array.isArray(response.items)) {
            setApplications(response.items);
          } else {
            console.error("Unexpected applications response format:", response);
            setApplications([]);
          }
          setLoadingApplications(false);
        },
        (error: any) => {
          console.error("Error fetching applications:", error);
          setLoadingApplications(false);
        }
      );
    }
  }, [baseType, apiKey]);

  // Fetch intents for selected application
  useEffect(() => {
    if (baseType === "application_intent" && applicationId && apiKey) {
      setLoadingIntents(true);
      ApplicationService.getApplicationActions(
        applicationId,
        setLoadingIntents,
        apiKey
      ).subscribe(
        (response: any) => {
          console.log("Intents response:", response);
          if (response && Array.isArray(response)) {
            setIntents(response);
          } else if (response && Array.isArray(response.items)) {
            setIntents(response.items);
          } else {
            console.error("Unexpected intents response format:", response);
            setIntents([]);
          }
          setLoadingIntents(false);
        },
        (error: any) => {
          console.error("Error fetching intents:", error);
          setLoadingIntents(false);
        }
      );
    }
  }, [baseType, applicationId, apiKey]);

  // Fetch applications for ignored_application_ids dropdown
  useEffect(() => {
    if (baseType === "intent_group" && apiKey) {
      ApplicationService.getApplications(
        setLoadingApplications,
        apiKey
      ).subscribe(
        (response: any) => {
          if (response && Array.isArray(response)) {
            setApplications(response);
          } else if (response && Array.isArray(response.items)) {
            setApplications(response.items);
          } else {
            console.error("Unexpected applications response format:", response);
            setApplications([]);
          }
        },
        (error: any) => {
          console.error("Error fetching applications:", error);
        }
      );
    }
  }, [baseType, apiKey]);

  const handleBaseTypeChange = (e: any) => {
    const newBaseType = e.target.value;
    onChange({
      base_type: newBaseType,
      intent_group_id: undefined,
      application_id: undefined,
      intent_id: undefined,
    });
  };

  const renderDropdownWithSpinner = (isLoading: boolean, isEmpty: boolean) => {
    if (isLoading) {
      return (
        <div style={{ textAlign: "center", padding: "10px" }}>
          <Spin size="small" /> Loading...
        </div>
      );
    }
    if (isEmpty) {
      return (
        <div style={{ textAlign: "center", padding: "10px", color: "#999" }}>
          No options available
        </div>
      );
    }
    return null;
  };

  return (
    <Space
      direction="vertical"
      size="large"
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
      }}>
      <StepHeader
        title="Choose Base Type"
        description="Select whether to use an Intent Group or an Application/Intent as the base for your dataset"
        style={{ textAlign: "center" }}
      />

      <Form layout="vertical" style={{ width: "500px" }}>
        <Form.Item label="Base Type" required>
          <Radio.Group
            value={baseType}
            onChange={handleBaseTypeChange}
            style={{ width: "100%" }}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Radio value="intent_group">Intent Group</Radio>
              <Radio value="application_intent">Application/Intent</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {baseType === "intent_group" && (
          <>
            <Form.Item
              label="Intent Group"
              required
              tooltip="Select an intent group for your dataset">
              <Select
                placeholder="Select an intent group"
                loading={loadingIntentGroups}
                value={intentGroupId}
                onChange={(value) => onChange({ intent_group_id: value })}
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                notFoundContent={renderDropdownWithSpinner(
                  loadingIntentGroups,
                  intentGroups.length === 0
                )}>
                {intentGroups.map((group) => (
                  <Select.Option key={group.id} value={group.id}>
                    {group.intent_name ||
                      group.name ||
                      `Intent Group ${group.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Ignored Applications"
              tooltip="Select applications to exclude from the dataset">
              <Select
                placeholder="Select applications to ignore"
                loading={loadingApplications}
                mode="multiple"
                onChange={(values) =>
                  onChange({ ignored_application_ids: values })
                }
                value={ignoredApplicationIds}
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                autoClearSearchValue={false}
                notFoundContent={renderDropdownWithSpinner(
                  loadingApplications,
                  applications.length === 0
                )}>
                {applications.map((app) => (
                  <Select.Option key={app.id} value={app.id}>
                    {app.application_name || `Application ${app.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        {baseType === "application_intent" && (
          <>
            <Form.Item
              label="Application"
              required
              tooltip="Select an application for your dataset">
              <Select
                placeholder="Select an application"
                loading={loadingApplications}
                value={applicationId}
                onChange={(value) => {
                  onChange({
                    application_id: value,
                    intent_id: undefined,
                  });
                }}
                style={{ width: "100%" }}
                showSearch
                optionFilterProp="children"
                notFoundContent={renderDropdownWithSpinner(
                  loadingApplications,
                  applications.length === 0
                )}>
                {applications.map((app) => (
                  <Select.Option key={app.id} value={app.id}>
                    {app.application_name || `Application ${app.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Intent"
              required
              tooltip="Select an intent for your dataset">
              <Select
                placeholder={
                  applicationId
                    ? "Select an intent"
                    : "Select an application first"
                }
                loading={loadingIntents}
                value={intentId}
                onChange={(value) => onChange({ intent_id: value })}
                style={{ width: "100%" }}
                disabled={!applicationId}
                showSearch
                optionFilterProp="children"
                notFoundContent={renderDropdownWithSpinner(
                  loadingIntents,
                  intents.length === 0 && applicationId !== undefined
                )}>
                {intents.map((intent) => (
                  <Select.Option key={intent.id} value={intent.id}>
                    {intent.action_type || intent.name || `Intent ${intent.id}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
      </Form>
    </Space>
  );
};

export default DatasetBaseType;
